import React, { useEffect, useMemo, useState } from 'react';
import { Loader, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { apiService } from '../../../../components/api/apiService';
import { llmConfigurationService, type LLMConfiguration } from '../../../../components/api/llmConfigurationService';
import EmptyState from '../../governed-mcp-gateway/components/EmptyState';
import {
  fieldClass,
  GatewayDrawer,
  ghostBtn,
  labelClass,
  panelClass,
  primaryBtn,
  SearchableSelect,
  selectField,
  tdClass,
  thClass,
  ToggleRow,
  trClass,
} from '../../governed-mcp-gateway/components/GatewayUi';
import { llmGatewayApi } from '../llmGatewayApi';
import { logLlmGateway } from '../llmGatewayLogger';
import type { GatewayIntent, IntentRoutingConfig } from '../llmGatewayTypes';

type ModelOption = {
  id: string;
  name: string;
  description?: string;
};

type IntentDraft = {
  id: string | null;
  name: string;
  description: string;
  examples: string[];
  target_model: string;
  enabled: boolean;
};

const EMPTY_CONFIG: IntentRoutingConfig = {
  intents: [],
  default_model: '',
  cache: { enabled: false, type: 'exact', ttl_seconds: 3600 },
};

const EXAMPLE_COUNT = 8;

const EXAMPLE_SYSTEM_PROMPT = `You are an expert semantic intent example generator for an LLM Gateway.

## Purpose

The LLM Gateway automatically routes incoming user requests to a target AI model based on the user's intent.

Customers configure an intent using:
- Intent Name: A short label identifying the request category.
- Intent Description: A detailed explanation of the requests that belong to this category.
- Target Model: The AI model selected when the intent matches.

Your responsibility is to generate realistic example user requests that accurately represent the given intent.

These examples will be embedded using \`sentence-transformers/all-MiniLM-L6-v2\` and stored in Qdrant for semantic similarity matching.

## Instructions

1. Treat the intent description as the primary definition of the intent.
2. Use the intent name as supporting context.
3. Generate requests that real users would naturally send to an AI assistant.
4. Include different ways of expressing the same intent, using varied vocabulary, phrasing, tone, and sentence structure.
5. Cover meaningful subcategories mentioned or clearly implied by the description.
6. Include a mixture of short, medium-length, and moderately detailed requests.
7. Prefer examples between 5 and 35 words.
8. Include questions, instructions, and conversational requests where appropriate.
9. Ensure each example clearly belongs to the specified intent.
10. Avoid generic, ambiguous, irrelevant, or overly broad examples.
11. Avoid near-duplicates and superficial word substitutions.
12. Do not invent new intent categories or expand the intent beyond its description.
13. If other configured intents are provided, avoid examples that primarily belong to those intents.
14. If existing examples are provided, generate additional distinct examples rather than repeating them.
15. Keep examples self-contained and understandable without conversation history.
16. Use the requested language, defaulting to English.
17. Do not mention internal routing, embeddings, vector databases, model providers, or infrastructure in generated examples unless the intent explicitly concerns those subjects.
18. Generate only the requested number of examples.

## Semantic quality

Prioritize semantic diversity over quantity.

Examples should cover distinct user goals and realistic variations within the intent, rather than repeating the same request with minor wording changes.

Avoid including negative examples or requests that do not belong to the intent.

## Output requirements

Return only a valid JSON object matching this schema:

{
  "intent_name": "string",
  "intent_description": "string",
  "examples": [
    "string"
  ]
}

Do not return Markdown, explanations, comments, confidence scores, vectors, or additional fields.

The intent name and description in the output must match the input exactly.`;

function parseExamplePayload(raw: string): string[] {
  const trimmed = raw.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  const jsonText = (fenced ? fenced[1] : trimmed).trim();
  let parsed: { examples?: unknown };
  try {
    parsed = JSON.parse(jsonText) as { examples?: unknown };
  } catch {
    const start = jsonText.indexOf('{');
    const end = jsonText.lastIndexOf('}');
    if (start === -1 || end <= start) throw new Error('The model did not return examples.');
    parsed = JSON.parse(jsonText.slice(start, end + 1)) as { examples?: unknown };
  }
  if (!Array.isArray(parsed.examples)) {
    throw new Error('The model did not return examples.');
  }
  return parsed.examples
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

function configLabel(item: LLMConfiguration): string {
  const extra = (item.additionalConfigJson || '').trim();
  if (extra.startsWith('{')) {
    try {
      const parsed = JSON.parse(extra) as { displayName?: string };
      if (parsed.displayName?.trim()) return parsed.displayName.trim();
    } catch {
      /* use modelName */
    }
  }
  return (item.modelName || '').trim() || extra || item.id;
}

function mergeExamples(current: string[], generated: string[]): string[] {
  const seen = new Set(current.map((item) => item.trim().toLowerCase()).filter(Boolean));
  const next = current.map((item) => item.trim()).filter(Boolean);
  generated.forEach((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    next.push(item);
  });
  return next.length ? next : [''];
}


function emptyDraft(): IntentDraft {
  return { id: null, name: '', description: '', examples: [''], target_model: '', enabled: true };
}

function draftFromIntent(intent: GatewayIntent): IntentDraft {
  return {
    id: intent.id,
    name: intent.name,
    description: intent.description,
    examples: intent.examples.length ? intent.examples : [''],
    target_model: intent.target_model,
    enabled: intent.enabled,
  };
}

export default function IntentRoutingTab({
  gatewayId,
  isLight,
  onToast,
}: {
  gatewayId: string;
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const [config, setConfig] = useState<IntentRoutingConfig>(EMPTY_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);
  const [providers, setProviders] = useState<ModelOption[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [draft, setDraft] = useState<IntentDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiDone, setAiDone] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDraft(null);
    setAiOpen(false);
    setConfigLoading(true);
    void llmGatewayApi
      .getIntentRouting(gatewayId)
      .then((next) => {
        if (!cancelled) setConfig(next);
      })
      .catch((error) => {
        if (!cancelled) onToast('error', error instanceof Error ? error.message : 'Could not load intent routing.');
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gatewayId, onToast]);

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setProvidersLoading(true);
      try {
        const data = await llmConfigurationService.getList();
        const list = Array.isArray(data) ? data : [];
        const active = list
          .filter((item) => item.isActive !== false && item.id)
          .map((item) => ({
            id: item.id,
            name: configLabel(item),
            description: item.region || item.endpointUrl || undefined,
          }));
        if (!cancelled) {
          setProviders(active);
          setProvidersError(active.length ? null : 'No models are available for this tenant.');
          logLlmGateway('app', 'intent_models_loaded', { count: active.length });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not load models.';
        logLlmGateway('error', 'intent_models_failed', { message });
        if (!cancelled) setProvidersError(message);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    };
    void loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const modelOptions = useMemo(
    () => providers.map((item) => ({ id: item.id, label: item.name, hint: item.description })),
    [providers]
  );

  const modelName = useMemo(() => {
    const names = new Map(providers.map((item) => [item.id, item.name]));
    return (id: string) => names.get(id) || id || '—';
  }, [providers]);

  const persistDefaults = async (next: IntentRoutingConfig) => {
    setConfig(next);
    try {
      const saved = await llmGatewayApi.saveIntentDefaults(gatewayId, {
        default_model: next.default_model,
        cache: next.cache,
      });
      setConfig(saved);
    } catch (error) {
      onToast('error', error instanceof Error ? error.message : 'Could not save routing defaults.');
    }
  };

  const openAdd = () => {
    setFormError(null);
    setAiOpen(false);
    setAiError(null);
    setAiDone(null);
    setDraft(emptyDraft());
    logLlmGateway('app', 'intent_add_open', { gatewayId });
  };

  const generateExamples = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    const description = draft.description.trim();
    if (!name || !description) {
      setAiError('Name and description are required.');
      return;
    }
    const existing = draft.examples.map((item) => item.trim()).filter(Boolean);
    const otherIntents = config.intents
      .filter((item) => item.id !== draft.id)
      .map((item) => `- ${item.name}: ${item.description || '(no description)'}`);
    setAiBusy(true);
    setAiError(null);
    setAiDone(null);
    logLlmGateway('app', 'intent_examples_generate', { gatewayId, existing: existing.length });
    try {
      const data = (await apiService.post(
        '/api/v1/copilot/chat',
        {
          temperature: 0.3,
          max_tokens: 800,
          messages: [
            { role: 'system', content: EXAMPLE_SYSTEM_PROMPT },
            {
              role: 'user',
              content: [
                'The user requirement is the intent name and description below.',
                `Intent name: ${name}`,
                `Intent description: ${description}`,
                existing.length
                  ? `Existing examples:\n${existing.map((item) => `- ${item}`).join('\n')}`
                  : 'Existing examples: (none)',
                otherIntents.length
                  ? `Other configured intents:\n${otherIntents.join('\n')}`
                  : 'Other configured intents: (none)',
                `Language: English`,
                `Generate ${EXAMPLE_COUNT} additional distinct examples.`,
              ].join('\n\n'),
            },
          ],
        },
        'backend_py'
      )) as { content?: string };
      const content = String(data?.content || '').trim();
      if (!content) throw new Error('The model returned an empty response.');
      const generated = parseExamplePayload(content);
      if (!generated.length) throw new Error('The model did not return examples.');
      setDraft((current) => (current ? { ...current, examples: mergeExamples(current.examples, generated) } : current));
      setAiDone(`${generated.length} examples added below. Generate again or close this.`);
      logLlmGateway('app', 'intent_examples_generate_ok', { gatewayId, count: generated.length });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not generate examples.';
      setAiError(message);
      logLlmGateway('error', 'intent_examples_generate_failed', { gatewayId, message });
    } finally {
      setAiBusy(false);
    }
  };

  const saveIntent = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    const examples = draft.examples.map((item) => item.trim()).filter(Boolean);
    if (!name) {
      setFormError('Name is required.');
      return;
    }
    if (!draft.target_model) {
      setFormError('Select a target model.');
      return;
    }
    setSaving(true);
    try {
      const saved = await llmGatewayApi.saveIntent(gatewayId, {
        id: draft.id || undefined,
        name,
        description: draft.description.trim(),
        examples,
        target_model: draft.target_model,
        enabled: draft.enabled,
      });
      setConfig(saved);
      setDraft(null);
      onToast('success', draft.id ? 'Intent updated.' : 'Intent added.');
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not save intent.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className={`${panelClass(isLight)} p-5`}>
        <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Routing defaults</h2>
        <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
          Used when no intent matches. Models come from registered provider types.
        </p>
        {providersLoading ? (
          <p className={`mt-3 flex items-center gap-2 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={14} className="animate-spin" />
            Loading models…
          </p>
        ) : null}
        {providersError ? <p className="mt-3 text-sm text-red-500">{providersError}</p> : null}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <SearchableSelect
            label="Default model"
            value={config.default_model}
            options={modelOptions}
            isLight={isLight}
            placeholder="Select a model"
            searchPlaceholder="Search models"
            onChange={(default_model) => void persistDefaults({ ...config, default_model })}
          />
          <label>
            <span className={labelClass(isLight)}>Cache TTL (seconds)</span>
            <input
              type="number"
              min={0}
              className={fieldClass(isLight)}
              value={config.cache.ttl_seconds}
              disabled={!config.cache.enabled}
              onChange={(event) =>
                void persistDefaults({
                  ...config,
                  cache: { ...config.cache, ttl_seconds: Math.max(0, Number(event.target.value) || 0) },
                })
              }
            />
          </label>
        </div>
        <div className="mt-2 grid gap-x-6 md:grid-cols-2">
          <ToggleRow
            label="Response cache"
            hint="Reuse an identical request within the TTL."
            checked={config.cache.enabled}
            isLight={isLight}
            onChange={(enabled) => void persistDefaults({ ...config, cache: { ...config.cache, enabled } })}
          />
          <label className="py-3">
            <span className={labelClass(isLight)}>Cache type</span>
            <select
              className={`${selectField(isLight)} w-full`}
              value={config.cache.type}
              disabled={!config.cache.enabled}
              onChange={() => void persistDefaults({ ...config, cache: { ...config.cache, type: 'exact' } })}
            >
              <option value="exact">Exact</option>
            </select>
          </label>
        </div>
      </section>

      <section className={`${panelClass(isLight)} overflow-hidden`}>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Intents</h2>
            <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Route a matching request to a specific model.
            </p>
          </div>
          <button type="button" className={primaryBtn()} onClick={openAdd}>
            <Plus size={14} />
            Add
          </button>
        </div>
        {configLoading ? (
          <p className={`flex items-center gap-2 px-5 py-10 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={14} className="animate-spin" />
            Loading intents…
          </p>
        ) : config.intents.length === 0 ? (
          <EmptyState
            isLight={isLight}
            title="No intents yet"
            description="Add an intent to send matching requests to a different model than the default."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={isLight ? 'bg-neutral-50 text-neutral-500' : 'bg-neutral-950 text-neutral-400'}>
                  <th className={thClass()}>Intent</th>
                  <th className={thClass()}>Examples</th>
                  <th className={thClass()}>Target model</th>
                  <th className={thClass()}>Enabled</th>
                  <th className={thClass()} />
                </tr>
              </thead>
              <tbody>
                {config.intents.map((intent) => (
                  <tr key={intent.id} className={trClass(isLight)}>
                    <td className={tdClass()}>
                      <p className={`font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{intent.name}</p>
                      {intent.description ? (
                        <p className={`mt-0.5 max-w-md text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                          {intent.description}
                        </p>
                      ) : null}
                    </td>
                    <td className={tdClass()}>
                      {intent.examples.length ? (
                        <ul className={`max-w-sm space-y-0.5 text-xs ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>
                          {intent.examples.map((example, index) => (
                            <li key={`${intent.id}-${index}`}>{example}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>—</span>
                      )}
                    </td>
                    <td className={tdClass()}>{modelName(intent.target_model)}</td>
                    <td className={tdClass()}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={intent.enabled}
                        aria-label={`${intent.enabled ? 'Disable' : 'Enable'} ${intent.name}`}
                        onClick={() => {
                          void llmGatewayApi
                            .saveIntent(gatewayId, { ...intent, enabled: !intent.enabled })
                            .then(setConfig)
                            .catch((error) =>
                              onToast('error', error instanceof Error ? error.message : 'Could not update intent.')
                            );
                        }}
                        className={`relative h-5 w-9 rounded-full ${intent.enabled ? 'bg-indigo-600' : isLight ? 'bg-neutral-300' : 'bg-neutral-700'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${intent.enabled ? 'translate-x-4' : ''}`} />
                      </button>
                    </td>
                    <td className={`${tdClass()} text-right`}>
                      <button
                        type="button"
                        className={ghostBtn(isLight)}
                        onClick={() => {
                          setFormError(null);
                          setAiOpen(false);
                          setAiError(null);
                          setAiDone(null);
                          setDraft(draftFromIntent(intent));
                        }}
                      >
                        <Pencil size={13} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`${ghostBtn(isLight)} ml-1`}
                        onClick={() => {
                          void llmGatewayApi
                            .deleteIntent(gatewayId, intent.id)
                            .then((next) => {
                              setConfig(next);
                              onToast('success', 'Intent removed.');
                            })
                            .catch((error) =>
                              onToast('error', error instanceof Error ? error.message : 'Could not remove intent.')
                            );
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <GatewayDrawer
        open={Boolean(draft)}
        title={draft?.id ? 'Edit intent' : 'Add intent'}
        subtitle="Name the intent, add example phrases, and choose the model that should handle it."
        isLight={isLight}
        widthClass="w-full max-w-4xl"
        onClose={() => {
          setAiOpen(false);
          setAiDone(null);
          setDraft(null);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={ghostBtn(isLight)} onClick={() => setDraft(null)}>
              Cancel
            </button>
            <button type="button" className={primaryBtn()} onClick={() => void saveIntent()} disabled={saving}>
              {saving ? <Loader size={14} className="animate-spin" /> : null}
              Save
            </button>
          </div>
        }
      >
        {draft ? (
          <div className="space-y-4">
            {formError ? <p className="text-sm text-red-500">{formError}</p> : null}
            <label className="block">
              <span className={labelClass(isLight)}>Name</span>
              <input
                className={fieldClass(isLight)}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </label>
            <div className="relative">
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className={`${labelClass(isLight)} mb-0`}>Description</span>
                <button
                  type="button"
                  className={`${ghostBtn(isLight)} h-8 px-2.5`}
                  aria-expanded={aiOpen}
                  aria-label="Generate examples"
                  onClick={() => {
                    setAiError(null);
                    setAiDone(null);
                    setAiOpen((open) => !open);
                  }}
                >
                  {aiBusy ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Generate
                </button>
              </div>
              <textarea
                rows={6}
                className={`${fieldClass(isLight)} min-h-[9rem] w-full`}
                placeholder="Requests to write, debug, explain, or optimize programming code."
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
              {aiOpen ? (
                <div
                  className={`absolute right-0 top-10 z-40 w-80 rounded-xl border p-3 shadow-xl ${
                    isLight ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-900'
                  }`}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Generate examples</p>
                  <p className={`mt-1 text-xs leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    Uses the name and description to add example requests. The description is left unchanged.
                  </p>
                  {aiError ? <p className="mt-2 text-xs text-red-500">{aiError}</p> : null}
                  {aiDone ? <p className={`mt-2 text-xs ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`}>{aiDone}</p> : null}
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" className={`${ghostBtn(isLight)} h-8`} onClick={() => setAiOpen(false)} disabled={aiBusy}>
                      Cancel
                    </button>
                    <button type="button" className={`${primaryBtn()} h-8`} onClick={() => void generateExamples()} disabled={aiBusy}>
                      {aiBusy ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      Generate
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <div>
              <span className={labelClass(isLight)}>Examples</span>
              <div className="space-y-2">
                {draft.examples.map((example, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      className={fieldClass(isLight)}
                      value={example}
                      placeholder="Write a Python function"
                      onChange={(event) => {
                        const examples = draft.examples.slice();
                        examples[index] = event.target.value;
                        setDraft({ ...draft, examples });
                      }}
                    />
                    <button
                      type="button"
                      className={ghostBtn(isLight)}
                      aria-label="Remove example"
                      onClick={() => {
                        const examples = draft.examples.filter((_, item) => item !== index);
                        setDraft({ ...draft, examples: examples.length ? examples : [''] });
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={`${ghostBtn(isLight)} mt-2`}
                onClick={() => setDraft({ ...draft, examples: [...draft.examples, ''] })}
              >
                <Plus size={13} />
                Add example
              </button>
            </div>
            <SearchableSelect
              label="Target model"
              value={draft.target_model}
              options={modelOptions}
              isLight={isLight}
              placeholder="Select a model"
              searchPlaceholder="Search models"
              menuPlacement="top"
              onChange={(target_model) => setDraft({ ...draft, target_model })}
            />
            <ToggleRow
              label="Enabled"
              checked={draft.enabled}
              isLight={isLight}
              onChange={(enabled) => setDraft({ ...draft, enabled })}
            />
          </div>
        ) : null}
      </GatewayDrawer>
    </div>
  );
}
